import { cn } from '@/lib/utils';

/**
 * How a service arrives at its price. The only question is WHO computes the
 * number: the operator types it, or the formula derives it.
 *
 * There is deliberately no third "time & materials" option. Hours x rate is the
 * labor term of the dynamic formula, not a peer of a flat price — offering it
 * as one is what let switching a flat service to "T&M" throw away the price the
 * operator had typed. See SPEC-line-item-model, "Flat vs Dynamic".
 */
export type PriceStrategy = 'FLAT' | 'DYNAMIC';

const STRATEGY_LABEL: Record<PriceStrategy, string> = {
  FLAT: 'Flat',
  DYNAMIC: 'Dynamic',
};

const STRATEGY_HINT: Record<PriceStrategy, string> = {
  FLAT: 'You set the price. Hours recorded on this service are cost only — they never move the price.',
  DYNAMIC:
    'The price is calculated: (hours x labor rate) + (material cost x markup) + (equipment cost x markup) + surcharges. The typed price is not used.',
};

interface PriceStrategyToggleProps {
  value: PriceStrategy;
  onChange: (strategy: PriceStrategy) => void;
  className?: string;
}

/**
 * Segmented Flat ↔ Dynamic control for a line item's price strategy. Extracted
 * from the inline toggle that used to live in LineItemPricingControls so the
 * price input can fill its grid column and align with Unit cost / Markup.
 */
export function PriceStrategyToggle({ value, onChange, className }: PriceStrategyToggleProps) {
  return (
    <div className={cn('flex shrink-0 items-center gap-0.5', className)}>
      {(['FLAT', 'DYNAMIC'] as const).map((s) => {
        const active = value === s;
        return (
          <button
            key={s}
            type="button"
            onClick={() => onChange(s)}
            title={STRATEGY_HINT[s]}
            className={cn(
              'rounded px-1.5 py-0.5 text-[11px] font-medium transition-colors',
              active
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            {STRATEGY_LABEL[s]}
          </button>
        );
      })}
    </div>
  );
}
